import { HttpClient } from '@angular/common/http';
import { inject, InjectionToken } from '@angular/core';
import {
  catchError,
  filter,
  map,
  Observable,
  of,
  retry,
  shareReplay,
  switchMap,
} from 'rxjs';
import { LayerManagementService } from './layer-management.service';

type LayerLegends = Record<string, LayerLegendGroup>;

interface LayerLegendGroup {
  layers: string[];
  legends: LayerLegend[];
}

export interface LayerLegend {
  name: string;
  src: string[];
}

export const LEGEND = new InjectionToken('legend.json', {
  factory: () => {
    const legendQuery$: Observable<LayerLegends | string> = inject(HttpClient)
      .get<LayerLegends>('assets/legend.json')
      .pipe(
        retry(1),
        catchError(() => {
          return of('Could not load legends');
        }),
        shareReplay(1),
      );

    const legendList$ = legendQuery$.pipe(
      filter((legends) => typeof legends !== 'string'),
      map((legends) =>
        Object.entries(legends).map(([key, value]) => ({
          name: key,
          ...value,
        })),
      ),
    );

    const activeLayers$ = inject(LayerManagementService).activeMapLayers$.pipe(
      map((layers) => layers.map((layer) => layer.name)),
      map((value) => new Set(value)),
    );

    return {
      values$: legendList$.pipe(
        switchMap((legendList) =>
          activeLayers$.pipe(
            map((activeLayers) =>
              legendList.filter((legendGroup) =>
                legendGroup.layers.some((layer) => activeLayers.has(layer)),
              ),
            ),
          ),
        ),
      ),
      error$: legendQuery$.pipe(filter((result) => typeof result === 'string')),
    };
  },
});
